import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';

// ============ Types ============

export interface VcfWorkstream {
  id: string;
  code: string;
  name: string;
  description: string | null;
  product: string | null;
  phase: 'assess' | 'design' | 'deploy' | 'migrate' | 'operate' | 'optimise' | null;
  pillar: 'infrastructure' | 'application' | 'security' | null;
  category: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VcfCapability {
  id: string;
  workstreamId: string;
  code: string;
  name: string;
  description: string | null;
  cls: 'CORE' | 'RECOMMENDED' | 'OPTIONAL' | null;
  defaultIncluded: boolean;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VcfActivity {
  id: string;
  capabilityId: string;
  name: string;
  description: string | null;
  role: string;
  skill: string | null;
  skillId: string | null;
  baseDays: number;
  activityPhase: string | null;
  isMilestone: boolean;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface VcfGuardrail {
  id: string;
  capabilityId: string;
  name: string;
  code: string;
  baseline: string | null;
  multiplier: string | null;
  baselineValue: number | null;
  multiplierFactor: number | null;
  multiplierStep: number | null;
  multiplierLabel: string | null;
  optionFactors: Record<string, number> | null;
  inputType: 'number' | 'select' | 'boolean';
  options: string[] | null;
  defaultValue: string | null;
  minValue: number | null;
  maxValue: number | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// ============ Input Types ============

interface CreateWorkstreamInput {
  code: string;
  name: string;
  description?: string;
  product?: string;
  phase?: 'assess' | 'design' | 'deploy' | 'migrate' | 'operate' | 'optimise';
  pillar?: 'infrastructure' | 'application' | 'security';
  category?: string;
  sortOrder?: number;
}

interface UpdateWorkstreamInput {
  code?: string;
  name?: string;
  description?: string;
  product?: string;
  phase?: 'assess' | 'design' | 'deploy' | 'migrate' | 'operate' | 'optimise';
  pillar?: 'infrastructure' | 'application' | 'security';
  category?: string;
  sortOrder?: number;
  isActive?: boolean;
}

interface CreateCapabilityInput {
  workstreamId: string;
  code: string;
  name: string;
  description?: string;
  cls?: 'CORE' | 'RECOMMENDED' | 'OPTIONAL';
  defaultIncluded?: boolean;
  sortOrder?: number;
}

interface UpdateCapabilityInput {
  code?: string;
  name?: string;
  description?: string;
  cls?: 'CORE' | 'RECOMMENDED' | 'OPTIONAL';
  defaultIncluded?: boolean;
  sortOrder?: number;
  isActive?: boolean;
}

interface CreateActivityInput {
  name: string;
  description?: string;
  role: string;
  skill?: string;
  skillId?: string;
  baseDays: number;
  activityPhase?: string;
  isMilestone?: boolean;
  sortOrder?: number;
}

interface UpdateActivityInput {
  name?: string;
  description?: string | null;
  role?: string;
  skill?: string | null;
  skillId?: string | null;
  baseDays?: number;
  activityPhase?: string | null;
  isMilestone?: boolean;
  sortOrder?: number;
  isActive?: boolean;
}

interface CreateGuardrailInput {
  name: string;
  code: string;
  baseline?: string;
  multiplier?: string;
  baselineValue?: number;
  multiplierFactor?: number;
  multiplierStep?: number;
  multiplierLabel?: string;
  optionFactors?: Record<string, number>;
  inputType?: 'number' | 'select' | 'boolean';
  options?: string[];
  defaultValue?: string;
  minValue?: number;
  maxValue?: number;
  sortOrder?: number;
}

interface UpdateGuardrailInput {
  name?: string;
  code?: string;
  baseline?: string | null;
  multiplier?: string | null;
  baselineValue?: number | null;
  multiplierFactor?: number | null;
  multiplierStep?: number | null;
  multiplierLabel?: string | null;
  optionFactors?: Record<string, number> | null;
  inputType?: 'number' | 'select' | 'boolean';
  options?: string[] | null;
  defaultValue?: string | null;
  minValue?: number | null;
  maxValue?: number | null;
  sortOrder?: number;
}

// ============ Response Types ============

interface WorkstreamResponse {
  workstream: VcfWorkstream;
}

interface CapabilityResponse {
  capability: VcfCapability;
}

interface ActivityResponse {
  activity: VcfActivity;
}

interface GuardrailResponse {
  guardrail: VcfGuardrail;
}

// ============ Workstream Mutations ============

export function useCreateWorkstream() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateWorkstreamInput) =>
      api.post<WorkstreamResponse>('/vcf/workstreams', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

export function useUpdateWorkstream() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & UpdateWorkstreamInput) =>
      api.patch<WorkstreamResponse>(`/vcf/workstreams/${id}`, input),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
      queryClient.invalidateQueries({ queryKey: ['workstreams', variables.id] });
    },
  });
}

export function useDeleteWorkstream() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/vcf/workstreams/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

// ============ Capability Mutations ============

export function useCreateCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCapabilityInput) =>
      api.post<CapabilityResponse>('/vcf/capabilities', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

export function useUpdateCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & UpdateCapabilityInput) =>
      api.patch<CapabilityResponse>(`/vcf/capabilities/${id}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

export function useDeleteCapability() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/vcf/capabilities/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

// ============ Activity Mutations ============

export function useCreateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ capabilityId, ...input }: { capabilityId: string } & CreateActivityInput) =>
      api.post<ActivityResponse>(`/vcf/capabilities/${capabilityId}/activities`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

export function useUpdateActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      capabilityId,
      activityId,
      ...input
    }: {
      capabilityId: string;
      activityId: string;
    } & UpdateActivityInput) =>
      api.patch<ActivityResponse>(
        `/vcf/capabilities/${capabilityId}/activities/${activityId}`,
        input
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

export function useDeleteActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ capabilityId, activityId }: { capabilityId: string; activityId: string }) =>
      api.delete(`/vcf/capabilities/${capabilityId}/activities/${activityId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

// ============ Guardrail Mutations ============

export function useCreateGuardrail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ capabilityId, ...input }: { capabilityId: string } & CreateGuardrailInput) =>
      api.post<GuardrailResponse>(`/vcf/capabilities/${capabilityId}/guardrails`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

export function useUpdateGuardrail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      capabilityId,
      guardrailId,
      ...input
    }: {
      capabilityId: string;
      guardrailId: string;
    } & UpdateGuardrailInput) =>
      api.patch<GuardrailResponse>(
        `/vcf/capabilities/${capabilityId}/guardrails/${guardrailId}`,
        input
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

export function useDeleteGuardrail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ capabilityId, guardrailId }: { capabilityId: string; guardrailId: string }) =>
      api.delete(`/vcf/capabilities/${capabilityId}/guardrails/${guardrailId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
    },
  });
}

// ============ Hard Delete with Reference Checking ============

export interface ActivityReferences {
  draftProjects: Array<{ id: string; name: string }>;
  dependencies: number;
  totalReferences: number;
}

export function useActivityReferences(capabilityId: string | null, activityId: string | null) {
  return useQuery({
    queryKey: ['activity-references', capabilityId, activityId],
    queryFn: () =>
      api.get<ActivityReferences>(`/vcf/capabilities/${capabilityId}/activities/${activityId}/references`),
    enabled: !!capabilityId && !!activityId,
  });
}

export function useDeleteActivityPermanent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ capabilityId, activityId }: { capabilityId: string; activityId: string }) =>
      api.delete(`/vcf/capabilities/${capabilityId}/activities/${activityId}/permanent`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
      queryClient.invalidateQueries({ queryKey: ['capabilities'] });
    },
  });
}

// ============ Dependencies ============

export function useActivityDependencies(capabilityId: string, activityId: string | null) {
  return useQuery({
    queryKey: ['dependencies', capabilityId, activityId],
    queryFn: () =>
      api.get<{ dependencies: Array<{ id: string; activityId: string; predecessorId: string; dependencyType: string; lagDays: number }> }>(
        `/vcf/capabilities/${capabilityId}/activities/${activityId}/dependencies`
      ),
    enabled: !!activityId,
  });
}

export function useAddDependency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ capabilityId, activityId, predecessorId, dependencyType, lagDays }: {
      capabilityId: string;
      activityId: string;
      predecessorId: string;
      dependencyType?: 'FS' | 'SS' | 'FF';
      lagDays?: number;
    }) =>
      api.post(`/vcf/capabilities/${capabilityId}/activities/${activityId}/dependencies`, {
        predecessorId, dependencyType, lagDays,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dependencies'] });
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
      queryClient.invalidateQueries({ queryKey: ['capability-sequence'] });
    },
  });
}

export function useDeleteDependency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ capabilityId, activityId, dependencyId }: {
      capabilityId: string;
      activityId: string;
      dependencyId: string;
    }) =>
      api.delete(`/vcf/capabilities/${capabilityId}/activities/${activityId}/dependencies/${dependencyId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dependencies'] });
      queryClient.invalidateQueries({ queryKey: ['workstreams'] });
      queryClient.invalidateQueries({ queryKey: ['capability-sequence'] });
    },
  });
}
